import { useState, memo } from 'react';
import { useUserCategories } from '../../hooks/useUserCategories';
import { useAuth } from '../../hooks/useAuth';

// ナガヤマのユーザーID（このユーザーが作成した科目・カテゴリは他ユーザーが削除・編集不可）
const NAGAYAMA_USER_ID = '34730814-27c6-476d-98d1-90d8c95649f1';

interface CategoryManagerProps {
  categoryType: 'subject' | 'category';
  selectedCategory: string;
  onSelectCategory: (category: string) => void;
  label: string;
  placeholder?: string;
}

export const CategoryManager = memo(({
  categoryType,
  selectedCategory,
  onSelectCategory,
  label,
  placeholder = 'カテゴリを選択',
}: CategoryManagerProps) => {
  const { user } = useAuth();
  const {
    categories,
    allCategories,
    loading,
    searchQuery,
    setSearchQuery,
    addCategory,
    toggleFavorite,
    deleteCategory,
  } = useUserCategories(categoryType);

  const [isOpen, setIsOpen] = useState(false);
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [error, setError] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      setError('カテゴリ名を入力してください');
      return;
    }

    try {
      await addCategory(newCategoryName.trim());
      setNewCategoryName('');
      setIsAddingNew(false);
      setError('');
    } catch (err: any) {
      if (err.code === '23505') {
        setError('このカテゴリは既に存在します');
      } else {
        setError('カテゴリの追加に失敗しました');
      }
    }
  };

  const handleToggleFavorite = async (e: React.MouseEvent, categoryId: string, isFavorite: boolean) => {
    e.stopPropagation();
    try {
      await toggleFavorite(categoryId, isFavorite);
    } catch (err) {
      console.error('お気に入り更新エラー:', err);
    }
  };

  const handleDeleteCategory = async (e: React.MouseEvent, categoryId: string) => {
    e.stopPropagation();
    if (confirmDeleteId === categoryId) {
      try {
        await deleteCategory(categoryId);
        setConfirmDeleteId(null);
      } catch (err) {
        console.error('カテゴリ削除エラー:', err);
      }
    } else {
      setConfirmDeleteId(categoryId);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
        {label}
      </label>

      {/* 選択ボックス */}
      <button
        type="button"
        onClick={() => { setIsOpen(!isOpen); setConfirmDeleteId(null); }}
        className="w-full px-4 py-2.5 text-left bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-sm hover:border-teal-500 dark:hover:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 transition-all duration-200 flex items-center justify-between"
      >
        <span className={selectedCategory ? 'text-gray-900 dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
          {selectedCategory || placeholder}
        </span>
        <i className={`ri-arrow-${isOpen ? 'up' : 'down'}-s-line text-gray-400`}></i>
      </button>

      {/* ドロップダウンメニュー */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl max-h-96 overflow-hidden flex flex-col">
          {/* 検索ボックス */}
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <div className="relative">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="カテゴリを検索..."
                className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 dark:text-white"
              />
            </div>
          </div>

          {/* カテゴリリスト */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                読み込み中...
              </div>
            ) : categories.length === 0 ? (
              <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                {searchQuery ? '該当するカテゴリがありません' : 'カテゴリがありません'}
              </div>
            ) : (
              <div className="py-2">
                {categories.map((cat) => {
                  const isMyRecord = user?.id === cat.user_id;
                  const isNagayamaRecord = cat.user_id === NAGAYAMA_USER_ID;
                  // 削除可能：自分のレコードで、かつナガヤマ作成でないか自分がナガヤマ本人
                  const canDelete = isMyRecord && (!isNagayamaRecord || user?.id === NAGAYAMA_USER_ID);
                  const isConfirming = confirmDeleteId === cat.id;
                  return (
                    <div
                      key={cat.id}
                      onClick={() => {
                        onSelectCategory(cat.category_name);
                        setIsOpen(false);
                        setConfirmDeleteId(null);
                      }}
                      className={`px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer flex items-center justify-between group ${
                        selectedCategory === cat.category_name ? 'bg-teal-50 dark:bg-teal-900/20' : ''
                      }`}
                    >
                      <span className="text-gray-900 dark:text-white flex items-center gap-2 text-sm">
                        {cat.is_favorite && (
                          <i className="ri-star-fill text-yellow-500 text-sm"></i>
                        )}
                        {cat.category_name}
                        {isNagayamaRecord && user?.id !== NAGAYAMA_USER_ID && (
                          <i className="ri-lock-fill text-xs text-amber-400" title="管理者作成（削除不可）"></i>
                        )}
                      </span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        {/* お気に入りボタン：全員表示 */}
                        <button
                          onClick={(e) => handleToggleFavorite(e, cat.id, cat.is_favorite)}
                          className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-600 rounded transition-colors"
                          title={cat.is_favorite ? 'お気に入り解除' : 'お気に入り登録'}
                        >
                          <i className={`${cat.is_favorite ? 'ri-star-fill text-yellow-500' : 'ri-star-line text-gray-400'} text-sm`}></i>
                        </button>
                        {/* 削除ボタン：削除権限がある場合のみ表示 */}
                        {canDelete && (
                          isConfirming ? (
                            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(null); }}
                                className="px-1.5 py-1 text-xs text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 rounded cursor-pointer whitespace-nowrap"
                              >
                                いいえ
                              </button>
                              <button
                                onClick={(e) => handleDeleteCategory(e, cat.id)}
                                className="px-1.5 py-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 hover:bg-red-100 rounded cursor-pointer whitespace-nowrap"
                              >
                                削除
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => handleDeleteCategory(e, cat.id)}
                              className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors"
                              title="削除"
                            >
                              <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                            </button>
                          )
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 新規追加セクション */}
          <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
            {isAddingNew ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  placeholder="新しいカテゴリ名"
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 dark:focus:ring-teal-400 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleAddCategory();
                    if (e.key === 'Escape') {
                      setIsAddingNew(false);
                      setNewCategoryName('');
                      setError('');
                    }
                  }}
                  autoFocus
                />
                {error && (
                  <p className="text-xs text-red-500">{error}</p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleAddCategory}
                    className="flex-1 px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    追加
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsAddingNew(false);
                      setNewCategoryName('');
                      setError('');
                    }}
                    className="flex-1 px-3 py-1.5 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                  >
                    キャンセル
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsAddingNew(true)}
                className="w-full px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-add-line"></i>
                新しいカテゴリを追加
              </button>
            )}
          </div>
        </div>
      )}

      {/* 背景オーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setIsOpen(false); setConfirmDeleteId(null); }}
        ></div>
      )}
    </div>
  );
});

CategoryManager.displayName = 'CategoryManager';